import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

// Cliente axios bare (sin token) para endpoints públicos /api/booking/*
const API = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });

function fmtSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-GT", { weekday: "long", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function PublicBooking() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [slots, setSlots] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const i = await API.get(`/booking/${token}`);
        setInfo(i.data);
        if (i.data.used) {
          setDone({ alreadyUsed: true });
          return;
        }
        const s = await API.get(`/booking/${token}/slots`);
        setSlots(s.data || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "El link no es válido o expiró.");
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const r = await API.post(`/booking/${token}`, { scheduled_at: chosen });
      setDone(r.data);
      toast.success("¡Confirmado!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error agendando");
    } finally { setBusy(false); }
  };

  if (error) return <PublicShell><div className="text-signal text-center py-6">{error}</div></PublicShell>;
  if (!info) return <PublicShell><div className="text-carbon-500 text-center py-6">Cargando…</div></PublicShell>;
  if (done?.alreadyUsed) return <PublicShell><div className="text-center py-6"><div className="text-2xl mb-2">✓</div><div className="text-sm text-carbon-600">Este link ya fue utilizado. Si necesitas reagendar, contacta al equipo.</div></div></PublicShell>;
  if (done) return (
    <PublicShell>
      <div className="text-center py-6">
        <div className="text-3xl mb-3">✓</div>
        <h2 className="font-heading text-xl mb-2">¡Listo, {info.user_first_name}!</h2>
        <p className="text-sm text-carbon-600 mb-4">
          Tu {info.kind === "interview" ? "entrevista" : info.kind === "induction" ? "capacitación" : "sesión"} quedó agendada para:
        </p>
        <div className="font-mono text-base bg-page border border-carbon-200 rounded py-3 px-4 inline-block capitalize">
          {fmtSlot(done.scheduled_at)}
        </div>
        <p className="mt-4 text-xs text-carbon-500">
          Un humano del equipo te enviará el link de la reunión por WhatsApp unos minutos antes.
        </p>
      </div>
    </PublicShell>
  );

  return (
    <PublicShell>
      <div className="mb-5">
        <div className="text-xs uppercase tracking-wider text-carbon-500 mb-1">
          {info.kind === "interview" ? "Entrevista" : info.kind === "induction" ? "Capacitación" : "Sesión"} · {info.duration_min} min
        </div>
        <h1 className="font-heading text-2xl">¡Hola{info.user_first_name ? `, ${info.user_first_name}` : ""}!</h1>
        <p className="text-sm text-carbon-600 mt-1">Elegí el horario que mejor te quede.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
        {slots.map((s) => (
          <button
            key={s.iso}
            onClick={() => setChosen(s.iso)}
            data-testid={`slot-${s.iso}`}
            className={`border rounded px-3 py-2.5 text-sm text-left transition-colors ${chosen === s.iso ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-carbon-200 hover:border-terracotta"}`}
          >
            <div className="font-mono text-xs">{s.label_utc}</div>
            <div className="text-xs text-carbon-500 capitalize">{fmtSlot(s.iso)}</div>
          </button>
        ))}
        {slots.length === 0 && <div className="text-sm text-carbon-400 col-span-full text-center py-6">Sin slots disponibles. Contacta al equipo.</div>}
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          onClick={submit}
          disabled={!chosen || busy}
          data-testid="public-book-confirm"
          className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-50"
        >
          {busy ? "Confirmando…" : "Confirmar horario"}
        </button>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-page flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      <div className="bg-surface border border-carbon-200 rounded-lg max-w-2xl w-full p-7 shadow-sm">
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-carbon-100">
          <div className="w-9 h-9 bg-terracotta rounded flex items-center justify-center font-heading font-bold text-white">HM</div>
          <div>
            <div className="font-heading text-base">Heroes Migrantes</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-carbon-500">Community Engine</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
